import {
  SAVED_DOCUMENT_VERSION,
  cloneValue,
  collectionKey,
  contractFor,
  decodePayload,
  defaultClock,
  defaultCreateId,
  exactFields,
  frozenClone,
  isRecord,
  isoTimestamp,
  opaqueId,
  providerFailure,
  savedPresetName,
  timestampRange,
} from "./preset-internals.js";

const DOCUMENT_FIELDS = Object.freeze([
  "collectionKey",
  "documentVersion",
  "presets",
  "revision",
]);
const PRESET_FIELDS = Object.freeze([
  "createdAt",
  "id",
  "name",
  "payload",
  "payloadRevisionId",
  "revision",
  "tombstone",
  "updatedAt",
]);

function invalid(reason) {
  return { kind: "invalid", reason };
}

function decodePreset(contract, raw) {
  if (!exactFields(raw, PRESET_FIELDS)) {
    throw new TypeError("Saved preset fields must match v1.");
  }
  if (typeof raw.tombstone !== "boolean") {
    throw new TypeError("Saved preset tombstone must be boolean.");
  }
  const timestamps = timestampRange(raw.createdAt, raw.updatedAt, "Saved preset");
  return {
    id: opaqueId(raw.id, "Saved preset ID"),
    revision: opaqueId(raw.revision, "Saved preset revision"),
    name: savedPresetName(raw.name),
    payloadRevisionId: opaqueId(raw.payloadRevisionId, "Saved payload revision ID"),
    payload: decodePayload(contract, raw.payload),
    tombstone: raw.tombstone,
    ...timestamps,
  };
}

export function decodeSavedPresetDocument(raw, contract, expectedCollectionKey) {
  if (raw === null || raw === undefined) {
    return { kind: "missing" };
  }
  if (!isRecord(raw)) {
    return invalid("saved-document-must-be-an-object");
  }
  if (
    Number.isSafeInteger(raw.documentVersion)
    && raw.documentVersion > SAVED_DOCUMENT_VERSION
  ) {
    return {
      kind: "unsupportedVersion",
      foundVersion: raw.documentVersion,
    };
  }
  if (!exactFields(raw, DOCUMENT_FIELDS)) {
    return invalid("saved-document-fields-must-match-v1");
  }
  if (raw.documentVersion !== SAVED_DOCUMENT_VERSION) {
    return invalid("saved-document-version-must-be-1");
  }
  let key;
  let revision;
  try {
    key = collectionKey(raw.collectionKey);
    revision = opaqueId(raw.revision, "Saved collection revision");
  } catch (_error) {
    return invalid("saved-document-identifiers-must-be-canonical");
  }
  if (key !== expectedCollectionKey) {
    return invalid("saved-document-collection-key-mismatch");
  }
  if (!Array.isArray(raw.presets)) {
    return invalid("saved-document-presets-must-be-an-array");
  }
  const seen = new Set();
  const presets = [];
  try {
    for (const rawPreset of raw.presets) {
      const preset = decodePreset(contract, rawPreset);
      if (seen.has(preset.id)) {
        throw new TypeError("Saved preset IDs must be unique.");
      }
      seen.add(preset.id);
      presets.push(preset);
    }
  } catch (_error) {
    return invalid("saved-presets-must-match-v1");
  }
  return { kind: "ready", collectionKey: key, revision, presets };
}

function storedDocument(state) {
  return {
    documentVersion: SAVED_DOCUMENT_VERSION,
    collectionKey: state.collectionKey,
    revision: state.revision,
    presets: state.presets.map((preset) => cloneValue(preset)),
  };
}

function liveProjection(state) {
  if (state.kind !== "ready") {
    return frozenClone(state);
  }
  return frozenClone({
    kind: "ready",
    collectionKey: state.collectionKey,
    revision: state.revision,
    presets: state.presets.filter((preset) => !preset.tombstone),
  });
}

function conflict(expectedRevision, state, expectedPresetRevision = undefined, actualPresetRevision = undefined) {
  return frozenClone({
    kind: "conflict",
    expectedRevision,
    actualRevision: state.kind === "ready" ? state.revision : null,
    ...(expectedPresetRevision === undefined ? {} : {
      expectedPresetRevision,
      actualPresetRevision: actualPresetRevision ?? null,
    }),
  });
}

function assertExpectedRevision(value) {
  if (value === undefined || value === null) {
    return value;
  }
  return opaqueId(value, "Expected saved collection revision");
}

export function createLocalSavedPresetRepository({
  provider,
  collectionContracts,
  clock = defaultClock,
  createId = defaultCreateId,
} = {}) {
  if (!provider || typeof provider.readSaved !== "function" || typeof provider.mutateSaved !== "function") {
    throw new TypeError("Saved preset repository requires a collection provider.");
  }
  if (typeof clock !== "function" || typeof createId !== "function") {
    throw new TypeError("Saved preset clock and ID factory must be functions.");
  }

  function nextId(prefix) {
    return opaqueId(createId(prefix), `${prefix} ID`);
  }

  async function load(rawKey) {
    const key = collectionKey(rawKey);
    const contract = contractFor(collectionContracts, key);
    try {
      return liveProjection(decodeSavedPresetDocument(await provider.readSaved(key), contract, key));
    } catch (error) {
      return frozenClone(providerFailure("read-saved-presets", error));
    }
  }

  async function mutate(rawKey, options, reducer) {
    const key = collectionKey(rawKey);
    const contract = contractFor(collectionContracts, key);
    const expectedRevision = assertExpectedRevision(options?.expectedRevision);
    try {
      return await provider.mutateSaved(key, (raw) => {
        const decoded = decodeSavedPresetDocument(raw, contract, key);
        if (decoded.kind === "invalid" || decoded.kind === "unsupportedVersion") {
          return { record: raw, result: frozenClone(decoded) };
        }
        if (
          expectedRevision !== undefined
          && expectedRevision !== (decoded.kind === "ready" ? decoded.revision : null)
        ) {
          return {
            record: raw,
            result: conflict(expectedRevision, decoded),
          };
        }
        const working = decoded.kind === "ready" ? cloneValue(decoded) : {
          kind: "ready",
          collectionKey: key,
          revision: nextId("saved_collection_revision"),
          presets: [],
        };
        const reduced = reducer(working, contract);
        if (reduced?.kind === "conflict") {
          return { record: raw, result: frozenClone(reduced) };
        }
        if (!isRecord(reduced) || !Array.isArray(reduced.presets)) {
          throw new TypeError("Saved preset reducer returned an invalid state.");
        }
        reduced.revision = nextId("saved_collection_revision");
        const result = liveProjection({ ...reduced, kind: "ready" });
        return { record: storedDocument(reduced), result };
      });
    } catch (error) {
      return frozenClone(providerFailure("write-saved-presets", error));
    }
  }

  async function create(rawKey, { name, payload, id, expectedRevision } = {}) {
    const key = collectionKey(rawKey);
    const createdId = id === undefined ? nextId("preset") : opaqueId(id, "Saved preset ID");
    const createdAt = isoTimestamp(clock(), "Saved preset creation time");
    const presetName = savedPresetName(name);
    let createdPreset = null;
    const state = await mutate(key, { expectedRevision }, (working, contract) => {
      if (working.presets.some((preset) => preset.id === createdId && !preset.tombstone)) {
        throw new TypeError(`Saved preset ${createdId} already exists.`);
      }
      createdPreset = {
        id: createdId,
        revision: nextId("preset_revision"),
        name: presetName,
        payloadRevisionId: nextId("saved_payload_revision"),
        payload: decodePayload(contract, payload),
        tombstone: false,
        createdAt,
        updatedAt: createdAt,
      };
      const priorIndex = working.presets.findIndex((preset) => preset.id === createdId);
      if (priorIndex >= 0) {
        working.presets[priorIndex] = createdPreset;
      } else {
        working.presets.push(createdPreset);
      }
      return working;
    });
    return state.kind === "ready"
      ? frozenClone({ ...state, effect: { kind: "preset-created", preset: createdPreset } })
      : state;
  }

  async function update(rawKey, presetId, {
    payload,
    name,
    expectedRevision,
    expectedPresetRevision,
  } = {}) {
    const key = collectionKey(rawKey);
    const id = opaqueId(presetId, "Saved preset ID");
    const expectedPreset = expectedPresetRevision === undefined
      ? undefined
      : opaqueId(expectedPresetRevision, "Expected preset revision");
    const updatedAt = isoTimestamp(clock(), "Saved preset update time");
    let updatedPreset = null;
    const state = await mutate(key, { expectedRevision }, (working, contract) => {
      const index = working.presets.findIndex((preset) => preset.id === id && !preset.tombstone);
      if (index < 0) {
        throw new TypeError(`Unknown saved preset ${id}.`);
      }
      const current = working.presets[index];
      if (expectedPreset !== undefined && current.revision !== expectedPreset) {
        return conflict(working.revision, working, expectedPreset, current.revision);
      }
      timestampRange(current.createdAt, updatedAt, "Saved preset");
      updatedPreset = {
        ...current,
        revision: nextId("preset_revision"),
        ...(name === undefined ? {} : {
          name: savedPresetName(name),
        }),
        ...(payload === undefined ? {} : {
          payload: decodePayload(contract, payload),
          payloadRevisionId: nextId("saved_payload_revision"),
        }),
        updatedAt,
      };
      working.presets[index] = updatedPreset;
      return working;
    });
    if (state.kind !== "ready") {
      return state;
    }
    return frozenClone({ ...state, effect: { kind: "preset-updated", preset: updatedPreset } });
  }

  function rename(rawKey, presetId, { name, expectedRevision, expectedPresetRevision } = {}) {
    return update(rawKey, presetId, {
      name,
      expectedRevision,
      expectedPresetRevision,
    });
  }

  async function remove(rawKey, presetId, {
    expectedRevision,
    expectedPresetRevision,
  } = {}) {
    const key = collectionKey(rawKey);
    const id = opaqueId(presetId, "Saved preset ID");
    const expectedPreset = expectedPresetRevision === undefined
      ? undefined
      : opaqueId(expectedPresetRevision, "Expected preset revision");
    const updatedAt = isoTimestamp(clock(), "Saved preset deletion time");
    let deletedRevision = null;
    const state = await mutate(key, { expectedRevision }, (working) => {
      const index = working.presets.findIndex((preset) => preset.id === id && !preset.tombstone);
      if (index < 0) {
        return working;
      }
      const current = working.presets[index];
      if (expectedPreset !== undefined && current.revision !== expectedPreset) {
        return conflict(working.revision, working, expectedPreset, current.revision);
      }
      timestampRange(current.createdAt, updatedAt, "Saved preset");
      deletedRevision = nextId("preset_revision");
      working.presets[index] = {
        ...current,
        revision: deletedRevision,
        tombstone: true,
        updatedAt,
      };
      return working;
    });
    if (state.kind !== "ready") {
      return state;
    }
    return frozenClone({
      ...state,
      effect: { kind: "preset-deleted", presetId: id, presetRevision: deletedRevision },
    });
  }

  async function reset(rawKey, { expectedRevision } = {}) {
    const key = collectionKey(rawKey);
    const expected = assertExpectedRevision(expectedRevision);
    try {
      return await provider.mutateSaved(key, (raw) => {
        const contract = contractFor(collectionContracts, key);
        const decoded = decodeSavedPresetDocument(raw, contract, key);
        if (
          expected !== undefined
          && expected !== (decoded.kind === "ready" ? decoded.revision : null)
        ) {
          return { record: raw, result: conflict(expected, decoded) };
        }
        return { record: null, result: frozenClone({ kind: "missing", collectionKey: key }) };
      });
    } catch (error) {
      return frozenClone(providerFailure("reset-saved-presets", error));
    }
  }

  async function importMany(rawKey, entries, { expectedRevision } = {}) {
    const key = collectionKey(rawKey);
    if (!Array.isArray(entries)) {
      throw new TypeError("Imported presets must be an array.");
    }
    const importedPresets = [];
    const state = await mutate(key, { expectedRevision }, (working, contract) => {
      const occupied = new Set(
        working.presets.filter((preset) => !preset.tombstone).map((preset) => preset.id),
      );
      for (const entry of entries) {
        if (!isRecord(entry)) {
          throw new TypeError("Imported preset must be an object.");
        }
        let presetId = opaqueId(entry.id, "Imported preset ID");
        if (occupied.has(presetId)) {
          presetId = nextId("preset");
        }
        occupied.add(presetId);
        const imported = {
          id: presetId,
          revision: nextId("preset_revision"),
          name: savedPresetName(entry.name, "Imported preset name"),
          payloadRevisionId: nextId("saved_payload_revision"),
          payload: decodePayload(contract, entry.payload),
          tombstone: false,
          createdAt: isoTimestamp(entry.createdAt, "Imported preset creation time"),
          updatedAt: isoTimestamp(entry.updatedAt, "Imported preset update time"),
        };
        const tombstoneIndex = working.presets.findIndex((preset) => (
          preset.id === presetId && preset.tombstone
        ));
        if (tombstoneIndex >= 0) {
          working.presets[tombstoneIndex] = imported;
        } else {
          working.presets.push(imported);
        }
        importedPresets.push(imported);
      }
      return working;
    });
    return state.kind === "ready"
      ? frozenClone({
        ...state,
        effect: {
          kind: "presets-imported",
          presets: importedPresets,
          presetIds: importedPresets.map((preset) => preset.id),
        },
      })
      : state;
  }

  return Object.freeze({ load, create, update, rename, remove, importMany, reset });
}
